import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
    dataLoadToastId,
    isDataLoadTimedOut,
    loadTimeoutMessage
} from "../utils/onboardingDataLoadGate";

/**
 * Auto-warns when any tracked data load stays in `loading` beyond `timeoutMs`.
 *
 * dataLoads shape: { [key: string]: { field: string, status: 'idle'|'loading'|'success'|'error', startedAt?: number } }
 */
export function useDataLoadTimeoutToasts(dataLoads, opts = {}) {
    const timeoutMs = typeof opts.timeoutMs === "number" ? opts.timeoutMs : 20000;

    const timeoutsRef = useRef(new Map()); // key -> timeoutId

    useEffect(() => {
        const loads = dataLoads || {};
        const now = Date.now();

        // Clear timers for keys that no longer exist.
        for (const key of Array.from(timeoutsRef.current.keys())) {
            if (!(key in loads)) {
                const id = timeoutsRef.current.get(key);
                clearTimeout(id);
                timeoutsRef.current.delete(key);
            }
        }

        for (const key of Object.keys(loads)) {
            const v = loads[key];
            const startedAt = typeof v?.startedAt === "number" ? v.startedAt : null;

            // If not loading, cancel any pending timer.
            if (v?.status !== "loading" || !v?.field || !startedAt) {
                const existing = timeoutsRef.current.get(key);
                if (existing) {
                    clearTimeout(existing);
                    timeoutsRef.current.delete(key);
                }
                continue;
            }

            // Already timed out -> warn (deduped by toastId) and no need to schedule.
            if (isDataLoadTimedOut(v, timeoutMs, now)) {
                toast.warn(loadTimeoutMessage(v.field), {
                    toastId: dataLoadToastId("timeout", v.field)
                });
                continue;
            }

            // Schedule warning at timeout boundary if not already scheduled.
            if (!timeoutsRef.current.has(key)) {
                const delay = Math.max(0, timeoutMs - (now - startedAt));
                const id = setTimeout(() => {
                    const latest = (dataLoads || {})[key];
                    if (!latest?.field || latest?.status !== "loading") return;
                    if (!isDataLoadTimedOut(latest, timeoutMs, Date.now())) return;
                    toast.warn(loadTimeoutMessage(latest.field), {
                        toastId: dataLoadToastId("timeout", latest.field)
                    });
                }, delay);
                timeoutsRef.current.set(key, id);
            }
        }

        return () => {
            for (const id of timeoutsRef.current.values()) clearTimeout(id);
            timeoutsRef.current.clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataLoads, timeoutMs]);
}
